import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { User, onAuthStateChanged } from 'firebase/auth';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { AuthBridgeService } from 'src/app/services/auth-bridge.service';

export type AuthPipeGenerator = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => any;

export const loggedIn: any = (user : User | null) => !!user;

@Injectable({
  providedIn: 'any'
})
export class AuthGuard implements CanActivate {

  constructor(
    private router: Router,
    private firebase: FirebaseService,
    private authBridge: AuthBridgeService,
  ) {}

  canActivate = async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    // If returning from centralized auth with a ?code, exchange it before
    // reading the auth state below. Shared/memoized so RoleGuard +
    // requireProductAccess (which run CONCURRENTLY with this guard) await the
    // same exchange instead of racing it — otherwise they read a null
    // currentUser before sign-in completes ("User not found").
    await this.authBridge.ensureReady();

    const authPipeFactory = next.data['authGuardPipe'] as AuthPipeGenerator || (() => loggedIn);
    const user = await this.retrieveUser();
    if (user && !(await this.isEmailVerified(user))) {
      // Unverified — sign out and hand off to centralized auth.
      await this.firebase.auth.signOut().catch(() => {});
      await this.authBridge.bridge.login();
      return false;
    }
    const can = authPipeFactory(next, state)(user);
    if (typeof can === 'boolean') {
      // On the authenticated success path, strip the OAuth callback params from
      // the URL. The bridge clears them via history.replaceState, but Angular
      // Router re-applies this navigation's query params when it finalizes — so
      // redirect to the clean path instead.
      if (can && user && next.queryParamMap.has('code')) {
        return this.router.parseUrl(state.url.split('?')[0]);
      }
      // A redirectLoggedInTo route (e.g. /login) with no session returns `true`
      // here — hand off to centralized auth instead of showing the local login.
      if (can && !user) {
        await this.authBridge.bridge.login();
        return false;
      }
      return can;
    } else if (Array.isArray(can)) {
      return this.router.createUrlTree(can);
    } else {
      // Redirect string: unauthenticated → centralized auth; otherwise honor it.
      if (!user) {
        await this.authBridge.bridge.login();
        return false;
      }
      return this.router.parseUrl(can);
    }
  };

  private async isEmailVerified(user: User): Promise<boolean> {
    if (user.emailVerified) return true;
    try {
      await user.reload();
    } catch {
      return true; // transient — don't lock the user out
    }
    return user.emailVerified;
  }

  retrieveUser(): Promise<User | null> {
    return new Promise((resolve, reject) => {
      const sub = onAuthStateChanged(this.firebase.auth, (user) => {
        sub();
        if (!user || user.isAnonymous) {
            resolve(null);
        } else {
            resolve(user);
        }
      }, () => {
        reject(null);
      });
    });
  }
}


export const redirectUnauthorizedTo: (redirect: string|any[]) => any =
  (redirect) => (user: User | null) => loggedIn(user) || redirect;
export const redirectLoggedInTo: (redirect: string|any[]) => any =
  (redirect) => (user: User | null) => loggedIn(user) && redirect || true;
